import { CheckBox, Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import styled from "styled-components/native";
import { css } from "styled-components/native";
import { copyStock, getRequestType, RequestTypes } from "./utils/utils";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { useTranslation } from "react-i18next";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import { StockItemRequestClass } from "@src/types/HistoryRequest";
import { ViewType } from "@components/molecules/PriceModeCard/types";
import { useTheme } from "styled-components/native";
import { useSelectedItems } from "../../contexts/SelectedItemsContext";
import { useSelector } from "react-redux";
import { Pressable, View } from "react-native";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { PdpSections } from "@utils/customization";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { formatNumberWithPrecision } from "@utils/product/productFormatters";
import { isMeleeItem } from "@utils/product/productValidators";

type ItemActionCardProps = {
  stockItemRequest: StockItemRequestClass;
  productType: any;
  setDisabledButton?: Dispatch<SetStateAction<boolean>>;
  validValue?: boolean;
  isModifyScreen?: boolean;
  displayCheckbox?: boolean;
  itemList?: any[];
  showListPrice?: boolean;
  isTotalOfferDiscountApplied?: boolean;
  hasAnyOfferDetails?: boolean;
  orderCartRequest?: boolean;
  fromRequestDetailScreen?: boolean;
};

const ItemActionCard = ({
  stockItemRequest,
  isModifyScreen = false,
  productType,
  setDisabledButton,
  showListPrice,
  isTotalOfferDiscountApplied,
  hasAnyOfferDetails,
  orderCartRequest,
  fromRequestDetailScreen = false,
}: ItemActionCardProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const userInfo = useUserInfo();
  const { selectedItems, setSelectedItems } = useSelectedItems();
  const isBulkRequest = !!stockItemRequest?.children;
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const hasChildren =
    stockItemRequest?.secondary_request_type === PdpSections.JEWELRY_BUILDER
      ? false
      : !!stockItemRequest?.children?.length;
  const isTotalOfferPriceApplied =
    stockItemRequest?.quote_details?.total_offer_price_applied;
  const pendingChildren = useMemo(
    () =>
      stockItemRequest?.children
        ?.filter((item: StockItemRequestClass) => item.status === "pending")
        .map((item: StockItemRequestClass) => item.id) || [],
    [stockItemRequest?.children]
  );

  const checkOfferApplied = () => {
    const itemTotal = Object.values(stockItemRequest?.children).reduce(
      (sum, item: any) =>
        sum +
        Number(
          item?.updated_total_sales_price || item?.offer_total_sales_price
        ),
      0
    );
    return (
      formatNumberWithPrecision(stockItemRequest?.offer_total_sales_price, {
        precision:
          userInfo?.roundUpPrices() &&
          !isMeleeItem(stockItemRequest?.[productType.productType])
            ? 0
            : 2,
      })?.replaceAll(",", "") !==
      formatNumberWithPrecision(itemTotal, {
        precision:
          userInfo?.roundUpPrices() &&
          !isMeleeItem(stockItemRequest?.[productType.productType])
            ? 0
            : 2,
      })?.replaceAll(",", "")
    );
  };

  const Wrapper =
    (showListPrice || isTotalOfferPriceApplied || checkOfferApplied()) &&
    isTotalOfferDiscountApplied === false &&
    !hasAnyOfferDetails
      ? Pressable
      : View;

  const showCheckbox = useMemo(
    () =>
      hasChildren &&
      pendingChildren?.length > 0 &&
      (stockItemRequest?.view_type !== ViewType.BuyerView ||
        isModifyScreen ||
        fromRequestDetailScreen),
    [
      hasChildren,
      pendingChildren,
      stockItemRequest?.view_type,
      isModifyScreen,
      fromRequestDetailScreen,
    ]
  );

  const isAllSelected = useMemo(() => {
    if (!hasChildren) return false;
    const pendingItems = stockItemRequest.children.filter(
      (item) => item.status === "pending"
    );
    return pendingItems.length === selectedItems.length;
  }, [stockItemRequest, selectedItems, hasChildren]);

  useEffect(() => {
    setDisabledButton?.(isBulkRequest ? !selectedItems.length : false);
  }, [isBulkRequest, selectedItems.length]);

  const handleSelectAll = () => {
    if (!hasChildren) return;
    if (stockItemRequest?.status !== "pending") {
      return;
    }
    if (
      stockItemRequest?.additional_attributes?.total_offer_price_applied ||
      getRequestType(stockItemRequest?.request_type) === RequestTypes.Buy ||
      getRequestType(stockItemRequest?.request_type) === RequestTypes.Quote
    ) {
      if (
        (isTotalOfferPriceApplied || checkOfferApplied()) &&
        isTotalOfferDiscountApplied === false &&
        !hasAnyOfferDetails
      ) {
        showToast(
          "",
          getRequestType(stockItemRequest?.request_type) === RequestTypes.Quote
            ? t("requestHistoryDetail.quote.quoteWarning.text")
            : t("requestHistoryDetail.buy.buyWarning.text"),
          ToastTypes.ERROR,
          true,
          true,
          80
        );
        return;
      }
    }

    if (isAllSelected) {
      setSelectedItems([]);
    } else {
      const allPendingItems = stockItemRequest.children
        .filter((item) => item.status === "pending")
        .map((item) => item.id);
      setSelectedItems(allPendingItems);
    }
  };

  useEffect(() => {
    if (hasChildren) {
      const initialSelectedItems = stockItemRequest?.children
        .filter((item) => item.status === "pending")
        .map((item) => item.id);
      setSelectedItems(initialSelectedItems);
    } else {
      setSelectedItems([]);
    }
  }, [stockItemRequest, hasChildren]);

  const getStockNumber = (item) => {
    const stockNum =
      enableJewelryBuilder && !orderCartRequest
        ? item[item.stock_item_type].stock_num
        : orderCartRequest
        ? item?.stock_num
        : item[productType.productType].stock_num;
    const parseStockNum = () => {
      if (!stockNum) return "";
      if (stockNum.includes("|")) {
        return stockNum
          .split("|")
          .map((num) => num.trim())
          .filter(Boolean);
      }
      return stockNum.trim();
    };
    return (
      (!isCustomApp && item.bmh_request_type !== "retail_buy_request"
        ? "VDB-"
        : "") + parseStockNum()
    );
  };

  return (
    <ActionsView displayCheckbox={showCheckbox ?? false}>
      {showCheckbox && (
        <CheckboxView showCheckbox={showCheckbox}>
          <Wrapper
            onPress={
              (isTotalOfferPriceApplied || checkOfferApplied()) &&
              isTotalOfferDiscountApplied === false &&
              !hasAnyOfferDetails
                ? handleSelectAll
                : undefined
            }
          >
            <CheckBox
              fillColor={theme["knob-bor"]}
              checkBoxSize={20}
              checkedFunction={handleSelectAll}
              isChecked={isAllSelected}
              disabled={
                (isTotalOfferPriceApplied || checkOfferApplied()) &&
                isTotalOfferDiscountApplied === false &&
                !hasAnyOfferDetails
                  ? true
                  : false
              }
            />
          </Wrapper>
          <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
            {t("requestHistoryDetail.selectionInfo.text", {
              selected: selectedItems.length,
              total: stockItemRequest.children.length,
            })}
          </VDBText>
        </CheckboxView>
      )}

      <CopyStockContainer
        onPress={() => {
          copyStock(
            stockItemRequest,
            hasChildren,
            getStockNumber,
            t,
            enableJewelryBuilder,
            orderCartRequest,
            selectedItems
          );
        }}
      >
        <VDBIcon
          variant={IconsVariant.VDBCustom}
          name={VDBIcons.COPY}
          size={SpaceType.Big}
          color={theme["main-01"]}
        />
        <Spacer size={SpaceType.ExtraSmall} />
        <VDBText
          type={
            theme.isLargeScreen ? typography["Body-R"] : typography["Minor-R"]
          }
          color={theme["core-06"]}
        >
          {t("requestHistoryDetail.copyStock.text")}
        </VDBText>
      </CopyStockContainer>
    </ActionsView>
  );
};

const ActionsView = styled.View<{ displayCheckbox: boolean }>`
  ${(displayCheckbox) =>
    displayCheckbox
      ? css`
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          padding-top: ${SpaceType.Basic}px;
          padding-bottom: ${SpaceType.Small}px;
        `
      : ""}
`;
const CheckboxView = styled.View<{
  showCheckbox?: boolean;
}>`
  padding-left: ${({ showCheckbox, theme }) =>
    showCheckbox
      ? theme.isMobile
        ? `${SpaceType.ExtraSmall}px`
        : `${SpaceType.Basic}px`
      : 0};
  align-items: center;
  flex-direction: row;
`;

const CopyStockContainer = styled.TouchableOpacity`
  flex-direction: row;
  align-items: center;
  flex: 1;
  justify-content: flex-end;
`;

export default ItemActionCard;
